import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  VStack,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Input,
  Button,
  useToast,
  Alert,
  AlertIcon,
  Spinner,
  HStack,
  Divider
} from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import jsPDF from 'jspdf';
import { supabase } from '../supabaseClient';

export default function DoctorAgreementSign() {
  const { doctorId } = useParams();
  const navigate = useNavigate();
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [signatureDataUrl, setSignatureDataUrl] = useState('');
  const [form, setForm] = useState({
    signed_name: '',
    signed_email: '',
    signed_phone: '',
    signed_npi: '',
    signed_license: '',
    signed_date: new Date().toLocaleDateString(),
  });
  const sigCanvas = useRef(null);
  const toast = useToast();

  useEffect(() => {
    fetchDoctor();
  }, [doctorId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDoctor = async () => {
    try {
      const { data, error } = await supabase
        .from('doctors')
        .select('*')
        .eq('id', doctorId)
        .single();

      if (error) throw error;

      if (data.agreement_signed) {
        // Already signed, redirect to thank you page
        window.location.href = 'https://athomeblooddraw.com/thank-you';
        return;
      }

      setDoctor(data);
      setForm(prev => ({
        ...prev,
        signed_name: data.full_name || '',
        signed_email: data.email || '',
        signed_phone: data.phone_number || '',
        signed_npi: data.npi_number || '',
        signed_license: data.medical_license || ''
      }));
    } catch (error) {
      toast({
        title: 'Error loading doctor information',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSignatureEnd = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      const dataUrl = sigCanvas.current.getCanvas().toDataURL();
      setSignatureDataUrl(dataUrl);
    }
  };

  const clearSignature = () => {
    sigCanvas.current.clear();
    setSignatureDataUrl('');
  };

  const previewPDF = async () => {
    if (!form.signed_name.trim()) {
      toast({
        title: 'Please enter your name first',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const signatureURL = sigCanvas.current && !sigCanvas.current.isEmpty() 
        ? sigCanvas.current.getCanvas().toDataURL('image/png') 
        : null;
      
      const pdfDoc = await generatePDF(signatureURL);
      pdfDoc.save(`Preview-Doctor-Agreement-${doctor.full_name.replace(/\s+/g, '-')}.pdf`);
      
      toast({
        title: 'PDF Preview Generated',
        description: 'The preview PDF has been downloaded.',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error generating preview',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.signed_name.trim()) {
      toast({
        title: 'Please enter your name',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (sigCanvas.current.isEmpty()) {
      toast({
        title: 'Please provide your signature',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSigning(true);
    try {
      // Convert signature to blob
      const canvas = sigCanvas.current.getCanvas();
      const signatureDataURL = canvas.toDataURL('image/png');
      
      // Convert data URL to blob
      const response = await fetch(signatureDataURL);
      const blob = await response.blob();
      
      // Upload signature to Supabase storage
      const fileName = `doctor-signature-${doctorId}-${Date.now()}.png`;
      const { error: uploadError } = await supabase.storage
        .from('doctor-signatures')
        .upload(fileName, blob, {
          contentType: 'image/png',
          upsert: true
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('doctor-signatures')
        .getPublicUrl(fileName);

      // Generate PDF with signature
      const pdfDoc = await generatePDF(signatureDataURL);
      const pdfBlob = pdfDoc.output('blob');
      
      // Upload PDF to Supabase storage
      const pdfFileName = `doctor-agreement-${doctorId}-${Date.now()}.pdf`;
      const { error: pdfUploadError } = await supabase.storage
        .from('doctor-signatures')
        .upload(pdfFileName, pdfBlob, {
          contentType: 'application/pdf',
          upsert: true
        });

      if (pdfUploadError) throw pdfUploadError;

      // Get PDF public URL
      const { data: pdfUrlData } = supabase.storage
        .from('doctor-signatures')
        .getPublicUrl(pdfFileName);

      // Update doctor record
      const { error: updateError } = await supabase
        .from('doctors')
        .update({
          agreement_signed: true,
          agreement_signed_at: new Date().toISOString(),
          agreement_signature_url: urlData.publicUrl,
          agreement_pdf_url: pdfUrlData.publicUrl,
          agreement_signed_name: form.signed_name,
          agreement_signed_email: form.signed_email,
          agreement_signed_phone: form.signed_phone,
          agreement_signed_npi: form.signed_npi,
          agreement_signed_license: form.signed_license,
          agreement_signed_date: form.signed_date
        })
        .eq('id', doctorId);

      if (updateError) throw updateError;

      // Download PDF for the user
      pdfDoc.save(`Doctor-Agreement-${doctor.full_name.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`);

      toast({
        title: 'Agreement signed successfully!',
        description: 'The signed agreement PDF has been downloaded and saved to our records.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Redirect to thank you page
      setTimeout(() => {
        window.location.href = 'https://athomeblooddraw.com/thank-you';
      }, 2000);

    } catch (error) {
      toast({
        title: 'Error signing agreement',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSigning(false);
    }
  };

  // Add a new function to generate PDF
  const generatePDF = async (signatureDataURL) => {
    const doc = new jsPDF();
    
    // Set up document properties
    doc.setProperties({
      title: 'Doctor Authorization Agreement',
      subject: 'Quality Laboratory Services Authorization',
      author: 'Quality Laboratory Services',
      creator: 'QLS Agreement System'
    });

    // Header with logo space and title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Quality Laboratory Services', 105, 20, { align: 'center' });
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.text('Authorization for Laboratory Processing', 105, 30, { align: 'center' });
    
    // Doctor Information Box
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Doctor Information:', 20, 45);
    
    doc.setFont('helvetica', 'normal');
    let yPos = 52;
    doc.text(`Doctor: ${doctor.full_name}`, 20, yPos);
    doc.text(`Clinic: ${doctor.clinic_name}`, 110, yPos);
    yPos += 7;
    doc.text(`Email: ${doctor.email}`, 20, yPos);
    doc.text(`NPI: ${doctor.npi_number}`, 110, yPos);
    
    // Physician Confirmation Section
    yPos = 75;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Physician Confirmation & Acknowledgment', 20, yPos);
    
    yPos += 10;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const confirmationText = 'This form confirms that the undersigned physician authorizes Quality Laboratory Services (QLS) to process blood specimens based on prescriptions they issue, including those that may indicate another laboratory as the preferred provider.';
    const confirmationLines = doc.splitTextToSize(confirmationText, 170);
    doc.text(confirmationLines, 20, yPos);
    yPos += confirmationLines.length * 5 + 5;

    // Agreement Terms
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Agreement Terms', 20, yPos);
    yPos += 10;

    const terms = [
      {
        title: '1. Authorization to Process Specimens',
        content: 'I authorize QLS to collect all blood specimens for my patients, regardless of whether the prescription form lists a different lab.'
      },
      {
        title: '2. Ongoing Application',
        content: 'This authorization applies to all future prescriptions I issue, unless I specify otherwise in writing.'
      },
      {
        title: '3. Right to Revoke',
        content: 'I may revoke this authorization at any time by providing written notice to QLS. Revocation will not affect any specimens already processed or in transit.'
      },
      {
        title: '4. Patient Billing Policy',
        content: 'Patients will not be billed by QLS for blood draws or processing, unless the prescription form is explicitly marked "Bill to Patient."'
      },
      {
        title: '5. Insurance & Denials',
        content: 'QLS will submit claims to the patient\'s insurance as applicable. If a claim is denied, the patient will not be responsible for payment unless prior consent was obtained or the form specifies billing to the patient.'
      },
      {
        title: '6. Patient Consent',
        content: 'I confirm that patients have provided appropriate verbal or written consent for their specimens to be collected and processed by QLS.'
      }
    ];

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    
    terms.forEach((term) => {
      // Check if we need a new page
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.setFont('helvetica', 'bold');
      doc.text(term.title, 20, yPos);
      yPos += 6;
      
      doc.setFont('helvetica', 'normal');
      const contentLines = doc.splitTextToSize(term.content, 170);
      doc.text(contentLines, 20, yPos);
      yPos += contentLines.length * 4 + 8;
    });

    // Physician Information & Signature Section
    if (yPos > 220) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Physician Information & Signature', 20, yPos);
    yPos += 15;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    
    // Physician details
    doc.text(`Physician Full Name: ${form.signed_name}`, 20, yPos);
    yPos += 8;
    doc.text(`NPI Number: ${form.signed_npi}`, 20, yPos);
    yPos += 8;
    doc.text(`Medical License Number: ${form.signed_license}`, 20, yPos);
    yPos += 8;
    doc.text(`Email: ${form.signed_email}`, 20, yPos);
    yPos += 8;
    doc.text(`Phone Number: ${form.signed_phone}`, 20, yPos);
    yPos += 15;

    // Signature section
    doc.text('Signature:', 20, yPos);
    doc.text(`Date: ${form.signed_date}`, 120, yPos);
    
    // Add signature image if available
    if (signatureDataURL) {
      try {
        // Add signature image
        doc.addImage(signatureDataURL, 'PNG', 20, yPos + 5, 80, 25);
      } catch (error) {
        console.error('Error adding signature to PDF:', error);
        // Draw a line if signature can't be added
        doc.line(20, yPos + 15, 100, yPos + 15);
      }
    } else {
      // Draw signature line
      doc.line(20, yPos + 15, 100, yPos + 15);
    }

    return doc;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Spinner size="xl" />
      </Box>
    );
  }

  if (!doctor) {
    return (
      <Box p={8} textAlign="center">
        <Alert status="error">
          <AlertIcon />
          Doctor not found or agreement already signed.
        </Alert>
      </Box>
    );
  }

  return (
    <Box maxW="1200px" mx="auto" p={6} bg="white" borderRadius="xl" boxShadow="xl" m={2}>
      <VStack spacing={2} align="stretch">
        {/* Compact Header */}
        <HStack justify="space-between" align="center" mb={1}>
          <HStack spacing={3} align="center">
            <img src="/qls-logo.png" alt="Quality Laboratory Services" style={{ height: '70px' }} />
            <Text fontSize="lg" color="gray.600" fontWeight="medium">
              Authorization for Laboratory Processing
            </Text>
          </HStack>
          
          <Box 
            bg="blue.100" 
            border="2px solid" 
            borderColor="blue.400" 
            borderRadius="xl" 
            p={5} 
            minW="600px"
            boxShadow="lg"
            height="70px"
            display="flex"
            flexDirection="column"
            justifyContent="center"
          >
            <VStack spacing={2} align="stretch" height="100%" justify="center">
              <HStack spacing={6} align="center">
                <HStack spacing={3} flex="1">
                  <Text fontSize="sm" fontWeight="semibold" color="gray.700" width="55px">
                    Doctor:
                  </Text>
                  <Text fontSize="md" color="gray.900" fontWeight="medium">
                    {doctor.full_name}
                  </Text>
                </HStack>
                <HStack spacing={3} flex="1">
                  <Text fontSize="sm" fontWeight="semibold" color="gray.700" width="45px">
                    Clinic:
                  </Text>
                  <Text fontSize="md" color="gray.900" fontWeight="medium">
                    {doctor.clinic_name}
                  </Text>
                </HStack>
              </HStack>
              <HStack spacing={6} align="center">
                <HStack spacing={3} flex="1">
                  <Text fontSize="sm" fontWeight="semibold" color="gray.700" width="55px">
                    Email:
                  </Text>
                  <Text fontSize="md" color="gray.900" fontWeight="medium">
                    {doctor.email}
                  </Text>
                </HStack>
                <HStack spacing={3} flex="1">
                  <Text fontSize="sm" fontWeight="semibold" color="gray.700" width="45px">
                    NPI:
                  </Text>
                  <Text fontSize="md" color="gray.900" fontWeight="medium">
                    {doctor.npi_number}
                  </Text>
                </HStack>
              </HStack>
            </VStack>
          </Box>
        </HStack>

        {/* Form Preview - Custom HTML Form */}
        <Box>
          <Box 
            border="1px solid" 
            borderColor="gray.300" 
            borderRadius="md" 
            overflow="auto"
            position="relative"
            height="850px"
            bg="white"
            p={6}
          >
            <VStack spacing={6} align="stretch">
              {/* Document Header */}
              <VStack spacing={4} align="center">
                <Heading size="lg" color="blue.700">
                  Quality Laboratory Services
                </Heading>
                <Text fontSize="lg" fontWeight="semibold">
                  Authorization for Laboratory Processing
                </Text>
              </VStack>

              {/* Physician Confirmation Section */}
              <Box>
                <Heading size="md" mb={4} color="gray.700">
                  Physician Confirmation & Acknowledgment
                </Heading>
                <Text mb={4}>
                  This form confirms that the undersigned physician authorizes{' '}
                  <Text as="span" fontWeight="bold">Quality Laboratory Services (QLS)</Text>{' '}
                  to process blood specimens based on prescriptions they issue, including those that may 
                  indicate another laboratory as the preferred provider.
                </Text>
              </Box>

              {/* Agreement Terms */}
              <Box>
                <Heading size="md" mb={4} color="gray.700">
                  Agreement Terms
                </Heading>
                
                <VStack spacing={4} align="stretch">
                  <Box>
                    <Text fontWeight="semibold" mb={2}>1. Authorization to Process Specimens</Text>
                    <Text>
                      I authorize QLS to collect all blood specimens for my patients, regardless of 
                      whether the prescription form lists a different lab.
                    </Text>
                  </Box>
                  
                  <Box>
                    <Text fontWeight="semibold" mb={2}>2. Ongoing Application</Text>
                    <Text>
                      This authorization applies to <Text as="span" fontWeight="bold">all future prescriptions</Text> I issue, unless I specify otherwise 
                      in writing.
                    </Text>
                  </Box>
                  
                  <Box>
                    <Text fontWeight="semibold" mb={2}>3. Right to Revoke</Text>
                    <Text>
                      I may revoke this authorization at any time by providing written notice to QLS. 
                      Revocation will not affect any specimens already processed or in transit.
                    </Text>
                  </Box>
                  
                  <Box>
                    <Text fontWeight="semibold" mb={2}>4. Patient Billing Policy</Text>
                    <Text>
                      Patients will <Text as="span" fontWeight="bold">not be billed</Text> by QLS for blood draws or processing, unless the prescription 
                      form is explicitly marked <Text as="span" fontWeight="bold">"Bill to Patient."</Text>
                    </Text>
                  </Box>
                  
                  <Box>
                    <Text fontWeight="semibold" mb={2}>5. Insurance & Denials</Text>
                    <Text>
                      QLS will submit claims to the patient's insurance as applicable. If a claim is denied, the 
                      patient will <Text as="span" fontWeight="bold">not be responsible</Text> for payment unless prior consent was obtained or the 
                      form specifies billing to the patient.
                    </Text>
                  </Box>
                  
                  <Box>
                    <Text fontWeight="semibold" mb={2}>6. Patient Consent</Text>
                    <Text>
                      I confirm that patients have provided appropriate verbal or written consent for their 
                      specimens to be collected and processed by QLS.
                    </Text>
                  </Box>
                </VStack>
              </Box>

              {/* Physician Information */}
              <Box>
                <Heading size="md" mb={4} color="gray.700">
                  Physician Information & Signature
                </Heading>
                
                <VStack spacing={4} align="stretch">
                  <HStack spacing={4}>
                    <Text fontWeight="semibold" minW="140px">Physician Full Name:</Text>
                    <Text fontWeight="bold" color={form.signed_name ? "black" : "gray.400"}>
                      {form.signed_name || "________________________________"}
                    </Text>
                  </HStack>
                  
                  <HStack spacing={4}>
                    <Text fontWeight="semibold" minW="140px">NPI Number:</Text>
                    <Text fontWeight="bold" color={form.signed_npi ? "black" : "gray.400"}>
                      {form.signed_npi || "________________"}
                    </Text>
                  </HStack>
                  
                  <HStack spacing={4}>
                    <Text fontWeight="semibold" minW="140px">Medical License Number:</Text>
                    <Text fontWeight="bold" color={form.signed_license ? "black" : "gray.400"}>
                      {form.signed_license || "____________"}
                    </Text>
                  </HStack>
                  
                  <HStack spacing={4}>
                    <Text fontWeight="semibold" minW="140px">Email:</Text>
                    <Text color={form.signed_email ? "black" : "gray.400"}>
                      {form.signed_email || "___________________________________"}
                    </Text>
                  </HStack>
                  
                  <HStack spacing={4}>
                    <Text fontWeight="semibold" minW="140px">Phone Number:</Text>
                    <Text fontWeight="bold" color={form.signed_phone ? "black" : "gray.400"}>
                      {form.signed_phone || "_______________"}
                    </Text>
                  </HStack>
                </VStack>
              </Box>

              {/* Signature Section */}
              <Box>
                <HStack spacing={8} mt={8}>
                  <VStack align="start" spacing={2}>
                    <Text fontWeight="semibold">Signature:</Text>
                    {/* Show signature if available, otherwise show line */}
                    {signatureDataUrl ? (
                      <Box
                        width="200px"
                        height="60px"
                        border="1px solid"
                        borderColor="gray.300"
                        borderRadius="md"
                        overflow="hidden"
                        bg="white"
                      >
                        <img
                          src={signatureDataUrl}
                          alt="Signature"
                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                      </Box>
                    ) : (
                      <Box width="200px" height="2px" bg="gray.400" />
                    )}
                  </VStack>
                  
                  <VStack align="start" spacing={2}>
                    <Text fontWeight="semibold">Date:</Text>
                    <Text fontWeight="bold" color={form.signed_date ? "black" : "gray.400"}>
                      {form.signed_date || "_______________________"}
                    </Text>
                  </VStack>
                </HStack>
              </Box>
            </VStack>
          </Box>
        </Box>

        <Divider />

        {/* Signature Form */}
        <form onSubmit={handleSubmit}>
          <VStack spacing={4} align="stretch">
            <Heading size="sm" color="blue.600">Electronic Signature</Heading>
            
            <Text fontSize="sm" color="blue.600" fontWeight="semibold" mb={2}>
              Fill in your information below - it will appear on the document above in real-time
            </Text>

            <HStack spacing={4} align="start">
              <VStack spacing={4} align="stretch" flex="1">
                <FormControl isRequired>
                  <FormLabel>Full Name</FormLabel>
                  <Input
                    name="signed_name"
                    value={form.signed_name}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Email Address</FormLabel>
                  <Input
                    name="signed_email"
                    value={form.signed_email}
                    onChange={handleInputChange}
                    placeholder="Enter your email address"
                    type="email"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Phone Number</FormLabel>
                  <Input
                    name="signed_phone"
                    value={form.signed_phone}
                    onChange={handleInputChange}
                    placeholder="Enter your phone number"
                    type="tel"
                  />
                </FormControl>
              </VStack>

              <VStack spacing={4} align="stretch" flex="1">
                <FormControl>
                  <FormLabel>NPI Number</FormLabel>
                  <Input
                    name="signed_npi"
                    value={form.signed_npi}
                    onChange={handleInputChange}
                    placeholder="Enter your NPI number"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Medical License Number</FormLabel>
                  <Input
                    name="signed_license"
                    value={form.signed_license}
                    onChange={handleInputChange}
                    placeholder="Enter your medical license number"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Date</FormLabel>
                  <Input
                    name="signed_date"
                    value={form.signed_date}
                    onChange={handleInputChange}
                    type="date"
                  />
                </FormControl>
              </VStack>
            </HStack>

            <FormControl isRequired>
              <FormLabel>Signature</FormLabel>
              <Box border="2px solid" borderColor="gray.300" borderRadius="md" p={2}>
                <SignatureCanvas
                  ref={sigCanvas}
                  onEnd={handleSignatureEnd}
                  canvasProps={{
                    width: 500,
                    height: 200,
                    className: 'signature-canvas',
                    style: { width: '100%', height: '200px' }
                  }}
                  backgroundColor="white"
                />
              </Box>
              <HStack mt={2}>
                <Button size="sm" onClick={clearSignature} variant="outline">
                  Clear Signature
                </Button>
                <Text fontSize="xs" color="gray.500">
                  Please sign above using your mouse or touchscreen
                </Text>
              </HStack>
            </FormControl>

            <Alert status="info">
              <AlertIcon />
              By signing this agreement, you authorize Quality Laboratory Services to process laboratory tests and agree to the terms outlined in the authorization document.
            </Alert>

            <HStack spacing={4} justify="center" pt={4}>
              <Button
                variant="outline"
                colorScheme="blue"
                size="lg"
                onClick={previewPDF}
              >
                Preview PDF
              </Button>
              <Button
                type="submit"
                colorScheme="blue"
                size="lg"
                isLoading={signing}
                loadingText="Signing..."
              >
                Sign Agreement
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => navigate('/')}
              >
                Cancel
              </Button>
            </HStack>
          </VStack>
        </form>
      </VStack>
    </Box>
  );
} 